/**
 * ProtectedRoute component.
 * Wraps routes that require authentication and/or specific roles.
 */

import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Container } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useAuthStore } from '@/stores/authStore';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Optional list of role names that are allowed to access this route.
   * If not specified, any authenticated user can access.
   * If specified, user must have at least one of these roles.
   */
  requiredRoles?: string[];
}

export default function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading, user, mustChangePassword, checkAuth } = useAuthStore();

  // Check authentication status on mount
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      checkAuth();
    }
  }, [isAuthenticated, isLoading, checkAuth]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to change password if required (except if already on change password page)
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" state={{ from: location }} replace />;
  }

  // Check role-based access if roles are specified
  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles = user?.roles.map((role) => role.name) || [];
    const hasRequiredRole = requiredRoles.some((role) => userRoles.includes(role));

    if (!hasRequiredRole) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <Container maxWidth="sm">
            <Box sx={{ textAlign: 'center' }}>
              <LockIcon sx={{ fontSize: 80, color: 'error.main', mb: 3 }} />
              <Typography variant="h4" gutterBottom>
                Access Denied
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                You do not have permission to access this page.
              </Typography>
              <Alert severity="error" sx={{ mt: 3 }}>
                Required roles: {requiredRoles.join(', ')}
                <br />
                Your roles: {userRoles.join(', ') || 'none'}
              </Alert>
            </Box>
          </Container>
        </Box>
      );
    }
  }

  // All checks passed, render children
  return <>{children}</>;
}
